import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Phone, Flame, Clock, Snowflake, CheckCircle2, AlertTriangle,
  PauseCircle, ArrowUpDown, RefreshCw, Users, Search,
  ListChecks, LayoutGrid, CalendarPlus, MessageCircle
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import LoggingPanel from "@/components/recall/LoggingPanel";
import BookFutureNeedDialog from "@/components/checkout/BookFutureNeedDialog";
import { recallDurationBreakdown } from "@/lib/recallDuration";

const PIPELINE_BUCKETS = [
  { key: "all", label: "All Patients", Icon: Users, hint: "Every patient currently in recall" },
  { key: "active", label: "Active", Icon: Phone, hint: "Ready to call or message" },
  { key: "waiting", label: "Waiting for Reply", Icon: MessageCircle, hint: "Messenger sent today" },
  { key: "cooling", label: "Cooling Down", Icon: Clock, hint: "Waiting before next attempt" },
  { key: "cold", label: "Cold Pile", Icon: Snowflake, hint: "Exhausted cycle, resurfaces later" },
  { key: "paused", label: "Paused", Icon: PauseCircle, hint: "Needs cleanup before outreach" },
  { key: "booked", label: "Booked", Icon: CheckCircle2, hint: "Recovered from recall" },
];

const QUEUE_TABS = [
  { key: "list", label: "Work now", Icon: ListChecks },
  { key: "pipeline", label: "Pipeline", Icon: LayoutGrid },
];

const LOAD_TABS = ["active", "waiting", "cooling", "cold", "paused", "booked"];
const RECALL_RPC_TAB_BY_BUCKET = {
  waiting: "all",
};

const RECALL_TUTORIAL_BASE_ROW = {
  tutorial_demo: true,
  patient_id: "tutorial-recall-patient",
  patient_name: "Maria Santos",
  patient_number: "DEMO-RC-001",
  patient_phone: "0917 555 0188",
  patient_email: "maria.santos@example.test",
  messenger_conversation_url: "https://m.me/tutorial-recall",
  current_state: "Active",
  cycle_count: 1,
  value_centavos: 1200000,
  procedure_count: 1,
  diagnosing_doctor_id: "tutorial-doctor",
  diagnosing_doctor_name: "Reyes",
  diagnosed_at: "2026-05-20T01:00:00.000Z",
  entered_queue_at: "2026-05-20T01:00:00.000Z",
  last_contacted_at: null,
  next_eligible_at: null,
  followup_at: null,
  contact_info_needs_update: false,
  no_messenger_reply: false,
  returning_from_cold: false,
  is_hot: true,
};

const RECALL_TUTORIAL_BOOKING_ROW = {
  ...RECALL_TUTORIAL_BASE_ROW,
  id: "tutorial-recall-booking",
  patient_id: "tutorial-recall-booking-patient",
  patient_name: "Maria Santos",
  patient_number: "DEMO-RC-001",
  tutorial_id: "recall-booking-card",
};

const RECALL_TUTORIAL_NO_ANSWER_ROW = {
  ...RECALL_TUTORIAL_BASE_ROW,
  id: "tutorial-recall-no-answer",
  patient_id: "tutorial-recall-no-answer-patient",
  patient_name: "Ramon Cruz",
  patient_number: "DEMO-RC-002",
  patient_phone: "0917 555 0190",
  patient_email: "ramon.cruz@example.test",
  value_centavos: 850000,
  tutorial_id: "recall-no-answer-card",
};

const RECALL_TUTORIAL_DECLINE_ROW = {
  ...RECALL_TUTORIAL_BASE_ROW,
  id: "tutorial-recall-decline",
  patient_id: "tutorial-recall-decline-patient",
  patient_name: "Lina Garcia",
  patient_number: "DEMO-RC-003",
  patient_phone: "0917 555 0191",
  patient_email: "lina.garcia@example.test",
  value_centavos: 650000,
  tutorial_id: "recall-decline-card",
};

const ACCEPTED_NOT_PAID_TUTORIAL_ROW = {
  ...RECALL_TUTORIAL_BASE_ROW,
  id: "tutorial-accepted-not-paid",
  patient_id: "tutorial-accepted-not-paid-patient",
  patient_name: "Juan Dela Cruz",
  patient_number: "DEMO-ANP-001",
  patient_phone: "0917 555 0123",
  patient_email: "juan.accepted@example.test",
  current_state: "Active",
  value_centavos: 2650000,
  balance_due_centavos: 1325000,
  amount_paid_centavos: 1325000,
  procedure_count: 2,
  diagnosing_doctor_name: "Reyes",
  diagnosed_at: "2026-05-27T02:00:00.000Z",
  entered_queue_at: "2026-05-29T02:30:00.000Z",
  next_appointment_at: "2026-06-15T02:00:00.000Z",
  treatment_status: "accepted",
  appointment_status: "scheduled",
  payment_status: "partially_paid",
  balance_status: "balance_due",
  tutorial_id: "recall-accepted-not-paid-card",
};

const RECALL_TUTORIAL_ROWS_BY_BUCKET = {
  active: [RECALL_TUTORIAL_BOOKING_ROW, RECALL_TUTORIAL_NO_ANSWER_ROW, RECALL_TUTORIAL_DECLINE_ROW],
  waiting: [],
  cooling: [],
  cold: [],
  paused: [],
  booked: [],
};

const RECALL_TUTORIAL_STATS = {
  unbooked_revenue_centavos: 2700000,
  in_queue: 3,
  cooling: 0,
  recovered_centavos: 0,
};

const ACCEPTED_NOT_PAID_ROWS_BY_BUCKET = {
  active: [ACCEPTED_NOT_PAID_TUTORIAL_ROW],
  waiting: [],
  cooling: [],
  cold: [],
  paused: [],
  booked: [],
};

const ACCEPTED_NOT_PAID_STATS = {
  unbooked_revenue_centavos: 2650000,
  in_queue: 1,
  cooling: 0,
  recovered_centavos: 0,
};

const RECALL_TUTORIAL_SETTINGS = {
  recall: {
    sort_mode: "conversion",
    cycle_count: 3,
  },
};

function cloneRowsByBucket(source) {
  return Object.fromEntries(
    LOAD_TABS.map((bucket) => [bucket, (source[bucket] || []).map((row) => ({ ...row }))])
  );
}

function moveTutorialRow(source, rowId, targetBucket, patch = {}) {
  const next = cloneRowsByBucket(source);
  let found = null;
  LOAD_TABS.forEach((bucket) => {
    next[bucket] = next[bucket].filter((row) => {
      if (row.id !== rowId) return true;
      found = row;
      return false;
    });
  });
  if (found && targetBucket) {
    next[targetBucket] = [...(next[targetBucket] || []), { ...found, ...patch }];
  }
  return next;
}

function tutorialStatsForRows(rowsByBucket) {
  const unbookedRows = ["active", "waiting", "cooling", "cold", "paused"]
    .flatMap((bucket) => rowsByBucket[bucket] || []);
  const bookedRows = rowsByBucket.booked || [];
  return {
    unbooked_revenue_centavos: unbookedRows.reduce((sum, row) => sum + Number(row.value_centavos || 0), 0),
    in_queue: (rowsByBucket.active || []).length,
    cooling: (rowsByBucket.cooling || []).length,
    recovered_centavos: bookedRows.reduce((sum, row) => sum + Number(row.value_centavos || 0), 0),
  };
}

function recallTutorialDemoKind() {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  if (params.get("demo") !== "1") return null;
  if (params.get("tutorial") === "recall-flow") return "recall-flow";
  if (params.get("tutorial") === "accepted-not-paid-today") return "accepted-not-paid-today";
  return null;
}

function formatMoney(centavos, currency = "PHP") {
  const symbol = currency === "PHP" ? "₱" : currency === "USD" ? "$" : `${currency} `;
  return symbol + Math.round((centavos ?? 0) / 100).toLocaleString("en-US");
}

function timeAgo(iso) {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "-";
  const diffMs = Date.now() - d.getTime();
  const m = Math.floor(diffMs / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function formatDateShort(iso) {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return "-"; }
}

function labelizeStatus(status) {
  return String(status || "")
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function continuationStatusesForRow(row) {
  return [row?.treatment_status, row?.appointment_status].filter(Boolean);
}

function moneyStatusesForRow(row) {
  return [row?.payment_status, row?.balance_status].filter(Boolean);
}

function hasFollowUpStatusSplit(row) {
  return continuationStatusesForRow(row).length > 0 || moneyStatusesForRow(row).length > 0;
}

function formatDateTimeShort(iso) {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return "-"; }
}

function appointmentDurationMinutes(appointment) {
  if (!appointment?.scheduled_start || !appointment?.scheduled_end) return 60;
  const start = new Date(appointment.scheduled_start);
  const end = new Date(appointment.scheduled_end);
  const minutes = Math.round((end.getTime() - start.getTime()) / 60_000);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : 60;
}

function formatDueShort(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return null;
  }
}

function followupDueAt(row) {
  if (row?.current_state !== "Follow-up Scheduled") return null;
  return row.followup_at || row.next_eligible_at || null;
}

function followupDueLabel(row) {
  const dueAt = followupDueAt(row);
  if (!dueAt) return null;
  const due = new Date(dueAt).getTime();
  if (!Number.isFinite(due) || due <= Date.now()) return null;
  return `Due ${formatDueShort(dueAt) || "later"}`;
}

function bucketLabel(key) {
  return PIPELINE_BUCKETS.find((bucket) => bucket.key === key)?.label || "All Patients";
}

function bucketForRow(row, fallback = "active") {
  if (row?.pipeline_bucket) return row.pipeline_bucket;
  const byState = {
    "Active": "active",
    "waiting_for_reply": "waiting",
    "Awaiting Reply": "waiting",
    "Follow-up Scheduled": "active",
    "Cooling Down": "cooling",
    "Cold Pile": "cold",
    "Paused": "paused",
    "Booked": "booked",
  };
  return byState[row?.current_state] || fallback;
}

function nextActionForRow(row) {
  if (!row) return "-";
  const dueLabel = followupDueLabel(row);
  if (dueLabel) return dueLabel;
  if (row.contact_info_needs_update) return "Fix contact info";
  if (row.no_messenger_reply) return "No Messenger reply";
  if (row.current_state === "Booked") return "Recovered";
  if (row.current_state === "waiting_for_reply" || row.current_state === "Awaiting Reply") return "Waiting for reply";
  if (row.current_state === "Cold Pile") return row.returning_from_cold ? "Call again" : "Wait for resurface";
  if (row.current_state === "Cooling Down") return "Wait for next cycle";
  if (row.current_state === "Follow-up Scheduled") return "Follow up";
  if (row.current_state === "Awaiting Reply") return "Check Messenger";
  if (row.current_state === "Paused") return "Review patient";
  return "Call patient";
}

function valueLabelForRow(row) {
  return row?.current_state === "Booked" ? "recovered" : "unbooked";
}

function recallWorkLabel(row) {
  const count = Number(row?.procedure_count || 0);
  if (row?.current_state === "Booked") {
    if (count > 0) return `${count} future need${count === 1 ? "" : "s"} booked`;
    return "Future needs booked";
  }
  return `${count} procedure${count === 1 ? "" : "s"}`;
}

function procedureForNeed(item) {
  const procedure = item?.procedures;
  return Array.isArray(procedure) ? procedure[0] : procedure;
}

function bookedNeedName(item) {
  return procedureForNeed(item)?.name || "Booked procedure";
}

function bookedNeedSite(item) {
  const parts = [];
  if (item?.tooth_number) parts.push(`#${item.tooth_number}`);
  if (item?.surface) parts.push(item.surface);
  return parts.join(" · ");
}

function bookedNeedLine(item) {
  const site = bookedNeedSite(item);
  return site ? `${bookedNeedName(item)} · ${site}` : bookedNeedName(item);
}

function bookedNeedFee(item) {
  return item?.fee_centavos_override ?? procedureForNeed(item)?.default_fee_centavos ?? 0;
}

function bookedRecallProcedureName(items) {
  if (!items?.length) return "Booked recall work";
  if (items.length === 1) return bookedNeedName(items[0]);
  return `${bookedNeedName(items[0])} + ${items.length - 1} more`;
}

function bookedWorkCardLabel(row, items, loading) {
  if (row?.current_state !== "Booked") return recallWorkLabel(row);
  if (loading) return "Loading booked work...";
  if (!items?.length) return recallWorkLabel(row);
  if (items.length === 1) return bookedNeedLine(items[0]);
  return `${bookedNeedLine(items[0])} + ${items.length - 1} more`;
}

function lastCallSummary(row) {
  if (row.cycle_count <= 1 && !row.last_contacted_at) return null;
  if (!row.last_contacted_at) return null;
  const ago = timeAgo(row.last_contacted_at);
  const stateGloss = {
    "Cooling Down": "No answer",
    "Cold Pile": "Cycle done",
    "waiting_for_reply": "Messenger sent",
    "Awaiting Reply": "Awaiting reply",
    "Follow-up Scheduled": "Wants to think",
    "Paused": "Wrong number",
    "Booked": "Booked",
  }[row.current_state] || "Contacted";
  return `Last: ${stateGloss} · ${ago}`;
}

function waitingReplyMeta(row) {
  const sentAt = row?.messenger_sent_at || row?.last_contacted_at;
  if (!sentAt) return { days: 0, stale: false, sentLabel: "-", staff: row?.messenger_sent_by_email || "Staff" };
  const sent = new Date(sentAt);
  const now = new Date();
  const sentDay = new Date(sent.getFullYear(), sent.getMonth(), sent.getDate()).getTime();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const days = Math.max(0, Math.floor((today - sentDay) / 86_400_000));
  return {
    days,
    stale: sentDay < today,
    sentLabel: formatDateTimeShort(sentAt),
    staff: row?.messenger_sent_by_email || "Staff",
  };
}

function useBookedRecallWork(row) {
  const [state, setState] = useState({ loading: false, items: [], appointmentId: null, appointment: null, bookedActivity: null });

  useEffect(() => {
    let cancelled = false;

    async function loadBookedWork() {
      if (!row?.id || row.current_state !== "Booked") {
        setState({ loading: false, items: [], appointmentId: null, appointment: null, bookedActivity: null });
        return;
      }

      if (row.tutorial_demo) {
        setState({
          loading: false,
          appointmentId: "tutorial-recall-appointment",
          appointment: {
            id: "tutorial-recall-appointment",
            scheduled_start: "2026-05-29T02:00:00.000Z",
            scheduled_end: "2026-05-29T03:15:00.000Z",
            status: "Scheduled",
          },
          bookedActivity: null,
          items: [{
            id: "tutorial-rct-46",
            tooth_number: "46",
            surface: null,
            status: "Scheduled",
            fee_centavos_override: 1200000,
            procedures: {
              name: "Root Canal Therapy",
              default_fee_centavos: 1200000,
              category: "endodontics",
            },
          }],
        });
        return;
      }

      setState((prev) => ({ ...prev, loading: true }));

      const activityResult = await supabase
        .from("activity_log")
        .select("metadata, occurred_at")
        .eq("target_table", "recall_queue")
        .eq("target_id", row.id)
        .eq("action_name", "recall.booked")
        .order("occurred_at", { ascending: false })
        .limit(1);

      if (cancelled) return;

      if (activityResult.error) {
        console.warn("[recall] booked activity:", activityResult.error.message);
        setState({ loading: false, items: [], appointmentId: null, appointment: null, bookedActivity: null });
        return;
      }

      const bookedActivity = activityResult.data?.[0] || null;
      const appointmentId = bookedActivity?.metadata?.appointment_id || null;
      if (!appointmentId) {
        setState({ loading: false, items: [], appointmentId: null, appointment: null, bookedActivity });
        return;
      }

      const appointmentResult = await supabase
        .from("appointments")
        .select("id, patient_id, doctor_id, chair_id, scheduled_start, scheduled_end, status, notes")
        .eq("id", appointmentId)
        .limit(1);

      if (cancelled) return;

      const appointment = Array.isArray(appointmentResult.data)
        ? appointmentResult.data[0] || null
        : null;

      const needsResult = await supabase
        .from("treatment_needs")
        .select(`
          id,
          tooth_number,
          surface,
          status,
          fee_centavos_override,
          created_at,
          procedures:procedure_id (
            name,
            default_fee_centavos,
            category,
            suggested_duration_minutes,
            uses_chair_setup
          )
        `)
        .eq("patient_id", row.patient_id)
        .eq("scheduled_appointment_id", appointmentId)
        .order("created_at", { ascending: false });

      if (cancelled) return;

      if (needsResult.error) {
        console.warn("[recall] booked treatment needs:", needsResult.error.message);
        setState({ loading: false, items: [], appointmentId, appointment, bookedActivity });
        return;
      }

      setState({
        loading: false,
        items: Array.isArray(needsResult.data) ? needsResult.data : [],
        appointmentId,
        appointment,
        bookedActivity,
      });
    }

    loadBookedWork();
    return () => { cancelled = true; };
  }, [row?.id, row?.patient_id, row?.current_state, row?.tutorial_demo]);

  return state;
}

function riskForRow(row) {
  if (!row) return { label: "Normal", tone: "slate" };
  if (row.contact_info_needs_update) return { label: "Bad number", tone: "amber" };
  if (row.no_messenger_reply) return { label: "No Messenger reply", tone: "rose" };
  if (row.is_hot) return { label: "Hot", tone: "rose" };
  if (row.returning_from_cold) return { label: "Returning", tone: "sky" };
  if (row.current_state === "Cold Pile") return { label: "Cold", tone: "slate" };
  if (row.current_state === "Booked") return { label: "Recovered", tone: "emerald" };
  return { label: "Normal", tone: "slate" };
}

export default function RecallCenter() {
  const { activeClinic, activeClinicId, clinicRole } = useAuth();
  const { activeBranchId } = useBranch();
  const tutorialDemoKind = recallTutorialDemoKind();
  const tutorialDemo = !!tutorialDemoKind;
  const [tab, setTab] = useState(tutorialDemo ? "pipeline" : "list");
  const [selectedBucket, setSelectedBucket] = useState(tutorialDemo ? "all" : "active");
  const [selectedRowId, setSelectedRowId] = useState("");
  const [query, setQuery] = useState("");
  const [rowsByBucket, setRowsByBucket] = useState({});
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingSort, setSavingSort] = useState(false);
  const [flowRow, setFlowRow] = useState(null);
  const [confirmationRow, setConfirmationRow] = useState(null);
  const [bookedDetailsRow, setBookedDetailsRow] = useState(null);
  const [changeBooking, setChangeBooking] = useState(null);
  const currency = activeClinic?.currency || "PHP";
  const sortMode = settings?.recall?.sort_mode || "conversion";
  const cycleCap = settings?.recall?.cycle_count || 3;
  const canWork = clinicRole && clinicRole !== "assistant";

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    if (tutorialDemo) {
      const tutorialRows = cloneRowsByBucket(
        tutorialDemoKind === "accepted-not-paid-today"
          ? ACCEPTED_NOT_PAID_ROWS_BY_BUCKET
          : RECALL_TUTORIAL_ROWS_BY_BUCKET,
      );
      setRowsByBucket(tutorialRows);
      setStats(tutorialDemoKind === "accepted-not-paid-today" ? ACCEPTED_NOT_PAID_STATS : RECALL_TUTORIAL_STATS);
      setSettings(RECALL_TUTORIAL_SETTINGS);
      setLoading(false);
      return;
    }

    const queueRequests = LOAD_TABS.map((bucket) =>
      supabase
        .rpc("list_recall_queue", {
          p_tab: RECALL_RPC_TAB_BY_BUCKET[bucket] || bucket,
          p_branch_id: activeBranchId || null,
        })
        .then((result) => ({ bucket, ...result }))
    );
    const [queueResults, statsResult, settingsResult] = await Promise.all([
      Promise.all(queueRequests),
      supabase.rpc("get_recall_stats", { p_branch_id: activeBranchId || null }),
      supabase.rpc("get_clinic_settings"),
    ]);

    const firstQueueError = queueResults.find((result) => result.error)?.error;
    if (firstQueueError) {
      setError(firstQueueError.message ?? "Could not load recall queue.");
      setRowsByBucket({});
      setLoading(false);
      return;
    }

    if (statsResult.error) console.warn("[recall] stats error:", statsResult.error.message);
    if (settingsResult.error) console.warn("[recall] settings error:", settingsResult.error.message);

    const nextRowsByBucket = {};
    queueResults.forEach((result) => {
      const rows = Array.isArray(result.data) ? result.data : [];
      nextRowsByBucket[result.bucket] = result.bucket === "waiting"
        ? rows.filter((row) => bucketForRow(row) === "waiting")
        : rows;
    });
    setRowsByBucket(nextRowsByBucket);
    setStats(statsResult.data ?? null);
    setSettings(settingsResult.data ?? null);
    setLoading(false);
  }, [activeBranchId, tutorialDemo, tutorialDemoKind]);

  const handleTutorialOutcome = useCallback((event) => {
    if (!tutorialDemo || !event?.rowId) return;
    const targetBucket =
      event.outcome === "booked" ? "booked"
      : event.outcome === "no_answer" ? "cooling"
      : event.outcome === "declined" ? "active"
      : "all";
    setRowsByBucket((current) => {
      let next = current;
      if (event.outcome === "booked") {
        next = moveTutorialRow(current, event.rowId, "booked", {
          current_state: "Booked",
          pipeline_bucket: "booked",
          tutorial_id: "recall-booked-card",
          last_contacted_at: "2026-05-27T03:00:00.000Z",
          next_eligible_at: null,
          followup_at: null,
          is_hot: false,
        });
      } else if (event.outcome === "no_answer") {
        next = moveTutorialRow(current, event.rowId, "cooling", {
          current_state: "Cooling Down",
          pipeline_bucket: "cooling",
          tutorial_id: "recall-cooling-card",
          cycle_count: 2,
          last_contacted_at: "2026-05-27T03:00:00.000Z",
          next_eligible_at: "2026-05-30T03:00:00.000Z",
          is_hot: false,
        });
      } else if (event.outcome === "declined") {
        next = moveTutorialRow(current, event.rowId, null);
      }
      setStats(tutorialStatsForRows(next));
      return next;
    });
    setSelectedBucket(targetBucket);
    setSelectedRowId("");
  }, [tutorialDemo]);

  useEffect(() => { if (activeClinicId) load(); }, [activeClinicId, load]);

  const allRows = useMemo(() => {
    const seen = new Set();
    const merged = [];
    LOAD_TABS.forEach((bucket) => {
      (rowsByBucket[bucket] || []).forEach((row) => {
        if (seen.has(row.id)) return;
        seen.add(row.id);
        merged.push({ ...row, pipeline_bucket: bucket });
      });
    });
    return merged;
  }, [rowsByBucket]);

  const bucketStats = useMemo(() => {
    const summary = {};
    PIPELINE_BUCKETS.forEach((bucket) => {
      const rows = bucket.key === "all" ? allRows : (rowsByBucket[bucket.key] || []);
      summary[bucket.key] = {
        count: rows.length,
        value_centavos: rows.reduce((sum, row) => sum + Number(row.value_centavos || 0), 0),
      };
    });
    return summary;
  }, [allRows, rowsByBucket]);

  const visibleRows = useMemo(() => {
    const source = selectedBucket === "all" ? allRows : (rowsByBucket[selectedBucket] || []);
    const term = query.trim().toLowerCase();
    if (!term) return source;
    return source.filter((row) => [
      row.patient_name,
      row.patient_number,
      row.patient_phone,
      row.patient_email,
      row.diagnosing_doctor_name,
      row.current_state,
    ].filter(Boolean).some((value) => String(value).toLowerCase().includes(term)));
  }, [allRows, rowsByBucket, selectedBucket, query]);

  const selectedRow = useMemo(() => {
    return visibleRows.find((row) => row.id === selectedRowId) || visibleRows[0] || null;
  }, [selectedRowId, visibleRows]);

  useEffect(() => {
    if (!visibleRows.length) {
      if (selectedRowId) setSelectedRowId("");
      return;
    }
    if (!visibleRows.some((row) => row.id === selectedRowId)) {
      setSelectedRowId(visibleRows[0].id);
    }
  }, [selectedRowId, visibleRows]);

  async function toggleSortMode() {
    if (savingSort) return;
    if (tutorialDemo) {
      toast.info("Tutorial mode keeps the demo queue local.");
      return;
    }
    setSavingSort(true);
    const next = sortMode === "conversion" ? "revenue" : "conversion";
    const { error: rpcErr } = await supabase.rpc("set_recall_settings", {
      p_payload: { sort_mode: next },
    });
    setSavingSort(false);
    if (rpcErr) {
      toast.error(rpcErr.message ?? "Couldn't change sort mode");
      return;
    }
    toast.success(`Sort: ${next === "conversion" ? "Conversion first" : "Revenue first"}`);
    load();
  }

  function openRecallFlow(row, bucket = bucketForRow(row, selectedBucket)) {
    if (!row) return;
    setSelectedBucket(bucket);
    setSelectedRowId(row.id);

    if (row.current_state === "Booked" || bucket === "booked") {
      setFlowRow(null);
      setConfirmationRow(null);
      setBookedDetailsRow({ ...row, pipeline_bucket: "booked" });
      return;
    }

    if (bucket === "active") {
      setConfirmationRow(null);
      setBookedDetailsRow(null);
      setFlowRow(row);
      return;
    }

    setFlowRow(null);
    setBookedDetailsRow(null);
    setConfirmationRow({ ...row, pipeline_bucket: bucket });
  }

  function confirmRecallFlow() {
    if (!confirmationRow) return;
    setFlowRow(confirmationRow);
    setConfirmationRow(null);
  }

  return (
    <div data-testid="recall-center-page" data-tutorial-id="recall-center-page" className="px-4 lg:px-6 py-5 space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3" data-testid="recall-header">
        <div>
          <h1 className="text-[22px] font-bold text-slate-900 tracking-tight">Recall Center</h1>
          <p className="text-[13px] text-slate-500 mt-0.5">
            Follow up before treatment is forgotten.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-300 bg-white text-[12px] text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            data-testid="recall-refresh"
          >
            <RefreshCw className={["w-3.5 h-3.5", loading ? "animate-spin" : ""].join(" ")} strokeWidth={2} />
            Refresh
          </button>
          <button
            type="button"
            onClick={toggleSortMode}
            disabled={savingSort || !canWork}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-300 bg-white text-[12px] text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            data-testid="recall-sort-toggle"
            title="Toggle queue sort"
          >
            <ArrowUpDown className="w-3.5 h-3.5" strokeWidth={2} />
            Sorted by: {sortMode === "conversion" ? "Conversion" : "Revenue"}
          </button>
        </div>
      </header>

      <StatsBar stats={stats} bucketStats={bucketStats} currency={currency} loading={loading} />

      {tutorialDemoKind === "accepted-not-paid-today" ? (
        <AcceptedNotPaidOwnerImpact currency={currency} />
      ) : null}

      <div className="flex items-center gap-1 border-b border-slate-200" data-testid="recall-view-tabs">
        {QUEUE_TABS.map(({ key, label, Icon }) => {
          const active = tab === key;
          return (
            <button
              key={key}
              type="button"
              data-testid={`recall-view-tab-${key}`}
              onClick={() => setTab(key)}
              className={[
                "inline-flex items-center gap-1.5 px-3 py-2 text-[13px] -mb-px border-b-2",
                active
                  ? "border-teal-500 text-teal-700 font-semibold"
                  : "border-transparent text-slate-500 hover:text-slate-800",
              ].join(" ")}
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={2.25} />
              {label}
            </button>
          );
        })}
      </div>

      <BucketRail
        selectedBucket={selectedBucket}
        onSelectBucket={setSelectedBucket}
        bucketStats={bucketStats}
      />

      {error ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-700 flex items-center gap-2" data-testid="recall-error">
          <AlertTriangle className="w-4 h-4" strokeWidth={2} />
          {error}
        </div>
      ) : null}

      <div data-testid="recall-workbench">
        <section className="min-w-0 rounded-lg border border-slate-200 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-3 py-2.5">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{tab === "pipeline" ? "Pipeline" : "Work now"}</div>
              <h2 className="text-[15px] font-bold text-slate-950">
                {selectedBucket === "active" ? "Ready now" : bucketLabel(selectedBucket)}
                <span className="ml-2 text-[12px] font-medium text-slate-500">
                  {visibleRows.length} patients
                </span>
              </h2>
            </div>
            <label className="relative block w-full sm:w-72">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" strokeWidth={2.25} />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search patient, doctor, status"
                data-testid="recall-search"
                className="h-9 w-full rounded-md border border-slate-300 bg-white pl-8 pr-3 text-[13px] text-slate-800 placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
            </label>
          </div>

          {loading ? (
            <div className="px-3 py-10 text-center text-[13px] text-slate-400" data-testid="recall-loading">Loading recall patients...</div>
          ) : tab === "pipeline" ? (
            <PipelineView
              rowsByBucket={rowsByBucket}
              selectedRowId={selectedRow?.id}
              onOpenRow={openRecallFlow}
              onSelectBucket={setSelectedBucket}
              currency={currency}
            />
          ) : visibleRows.length === 0 ? (
            <EmptyState bucket={selectedBucket} />
          ) : (
            <RecallWorkList
              rows={visibleRows}
              currency={currency}
              cycleCap={cycleCap}
              onOpenRow={(row) => openRecallFlow(row, bucketForRow(row, selectedBucket))}
            />
          )}
        </section>
      </div>

      <RecallFlowModal
        row={flowRow}
        currency={currency}
        cycleCap={cycleCap}
        canWork={canWork}
        tutorialDemo={tutorialDemo}
        onClose={() => setFlowRow(null)}
        onAfterAction={() => {
          setFlowRow(null);
          if (!tutorialDemo) load();
        }}
        onTutorialOutcome={handleTutorialOutcome}
      />

      <RecallBucketConfirmation
        row={confirmationRow}
        onCancel={() => setConfirmationRow(null)}
        onConfirm={confirmRecallFlow}
      />

      <BookedRecallDetails
        row={bookedDetailsRow}
        currency={currency}
        onClose={() => setBookedDetailsRow(null)}
        onChangeBooking={(row, bookedWork) => {
          setBookedDetailsRow(null);
          if (!bookedWork?.appointmentId || !bookedWork?.appointment) {
            toast.error("Could not find the booked appointment to change.");
            return;
          }
          setChangeBooking({ row, bookedWork });
        }}
      />

      <RecallChangeBookingDialog
        context={changeBooking}
        currency={currency}
        onClose={() => setChangeBooking(null)}
        onChanged={() => {
          setChangeBooking(null);
          load();
        }}
      />
    </div>
  );
}

function StatsBar({ stats, bucketStats, currency, loading }) {
  const ready = bucketStats.active || { count: 0, value_centavos: 0 };
  const recovered = stats?.recovered_centavos;
  return (
    <section
      className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3"
      data-testid="recall-stats"
      data-tutorial-id="recall-stats"
    >
      <div className="flex min-w-0 items-center gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-teal-50 text-teal-700">
          <Phone className="h-4 w-4" strokeWidth={2.25} />
        </span>
        <div>
          <div className="text-[13px] font-bold text-slate-950">
            {loading ? "Loading queue..." : `${ready.count} ready now`}
          </div>
          <p className="mt-0.5 text-[12px] text-slate-500">
            {loading ? "" : `${formatMoney(ready.value_centavos, currency)} waiting to be recovered`}
          </p>
        </div>
      </div>
      <div className="border-l border-slate-200 pl-4 text-right">
        <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Recovered this month</div>
        <div className="mt-0.5 text-[16px] font-bold tabular-nums text-emerald-700">
          {loading || recovered == null ? "-" : formatMoney(recovered, currency)}
        </div>
      </div>
    </section>
  );
}

function AcceptedNotPaidOwnerImpact({ currency }) {
  return (
    <section
      className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
      data-testid="recall-accepted-not-paid-owner-impact"
      data-tutorial-id="recall-accepted-not-paid-owner-impact"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-wide text-amber-800">Owner view impact</p>
          <h2 className="mt-1 text-[15px] font-bold text-slate-950">Accepted treatment, unpaid balance, owned follow-up</h2>
          <p className="mt-1 max-w-3xl text-[13px] leading-5 text-amber-950">
            This is not a failed checkout. The patient accepted treatment and has a scheduled next visit; the clinic still owns the balance due.
          </p>
        </div>
        <div className="grid min-w-[260px] grid-cols-2 gap-2 text-right">
          <OwnerImpactMetric label="Balance due" value={formatMoney(1325000, currency)} />
          <OwnerImpactMetric label="Next visit" value="Jun 15" />
        </div>
      </div>
    </section>
  );
}

function OwnerImpactMetric({ label, value }) {
  return (
    <div className="rounded-md border border-amber-200 bg-white/70 px-3 py-2">
      <div className="text-[10px] font-bold uppercase tracking-wide text-amber-700">{label}</div>
      <div className="mt-0.5 text-sm font-bold tabular-nums text-slate-950">{value}</div>
    </div>
  );
}

function BucketRail({ selectedBucket, onSelectBucket, bucketStats }) {
  return (
    <div className="overflow-x-auto" data-testid="recall-bucket-rail" data-tutorial-id="recall-bucket-rail">
      <div className="flex min-w-max items-center gap-2 pb-1">
        {PIPELINE_BUCKETS.map((bucket) => {
          const active = selectedBucket === bucket.key;
          const Icon = bucket.Icon;
          const stats = bucketStats[bucket.key] || { count: 0, value_centavos: 0 };
          return (
            <button
              key={bucket.key}
              type="button"
              onClick={() => onSelectBucket(bucket.key)}
              data-testid={`recall-bucket-${bucket.key}`}
              data-tutorial-id={`recall-bucket-${bucket.key}`}
              title={bucket.hint}
              className={[
                "inline-flex h-9 items-center gap-1.5 rounded-full border px-3 text-[12px] font-semibold transition",
                active
                  ? "border-teal-600 bg-teal-600 text-white shadow-sm"
                  : "border-slate-200 bg-white text-slate-600 hover:border-teal-200 hover:text-teal-800",
              ].join(" ")}
            >
              <Icon className="h-3.5 w-3.5" strokeWidth={2.25} />
              <span>{bucket.key === "all" ? "All" : bucket.key === "active" ? "Ready" : bucket.label}</span>
              <span className={[
                "rounded-full px-1.5 py-0.5 text-[11px] font-bold tabular-nums",
                active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600",
              ].join(" ")}>{stats.count}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PipelineView({ rowsByBucket, selectedRowId, onOpenRow, onSelectBucket, currency }) {
  return (
    <div className="overflow-x-auto p-3" data-testid="recall-pipeline" data-tutorial-id="recall-pipeline">
      <div className="grid min-w-[1240px] grid-cols-6 gap-3">
        {PIPELINE_BUCKETS.filter((bucket) => bucket.key !== "all").map((bucket) => {
          const rows = rowsByBucket[bucket.key] || [];
          return (
            <section key={bucket.key} className="rounded-lg border border-slate-200 bg-slate-50">
              <button
                type="button"
                onClick={() => onSelectBucket(bucket.key)}
                className="flex w-full items-center justify-between border-b border-slate-200 px-3 py-2 text-left"
              >
                <span className="text-[12px] font-bold text-slate-800">{bucket.label}</span>
                <span className="rounded-md bg-white px-2 py-0.5 text-[11px] font-bold text-slate-600 ring-1 ring-slate-200">{rows.length}</span>
              </button>
              <div className="space-y-2 p-2">
                {rows.length ? rows.slice(0, 8).map((row) => (
                  <RecallMiniCard
                    key={row.id}
                    row={row}
                    currency={currency}
                    selected={row.id === selectedRowId}
                    onClick={() => {
                      onSelectBucket(bucket.key);
                      onOpenRow(row, bucket.key);
                    }}
                  />
                )) : (
                  <div className="rounded-md border border-dashed border-slate-200 bg-white px-3 py-8 text-center text-[12px] text-slate-400">No patients</div>
                )}
                {rows.length > 8 ? (
                  <button type="button" onClick={() => onSelectBucket(bucket.key)} className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-[12px] font-semibold text-slate-600 hover:bg-slate-50">
                    View {rows.length - 8} more
                  </button>
                ) : null}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function RecallMiniCard({ row, currency, selected, onClick }) {
  const risk = riskForRow(row);
  const dueLabel = followupDueLabel(row);
  const bookedWork = useBookedRecallWork(row);
  const waitingMeta = bucketForRow(row) === "waiting" ? waitingReplyMeta(row) : null;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`recall-mini-card-${row.id}`}
      data-tutorial-id={row.tutorial_id || undefined}
      className={[
        "w-full rounded-md border bg-white p-2 text-left transition",
        selected ? "border-teal-300 ring-2 ring-teal-100" : "border-slate-200 hover:border-teal-200",
      ].join(" ")}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-[13px] font-bold text-slate-950">{row.patient_name || "Unnamed"}</div>
          <div className="mt-0.5 truncate text-[11px] text-slate-500">
            {bookedWorkCardLabel(row, bookedWork.items, bookedWork.loading)}
          </div>
        </div>
        {risk.tone === "rose" ? <Flame className="h-3.5 w-3.5 shrink-0 text-rose-500" strokeWidth={2.25} /> : null}
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-[12px] font-bold tabular-nums text-amber-700">{formatMoney(row.value_centavos, currency)}</span>
        <span className={["text-[11px]", dueLabel ? "font-semibold text-violet-700" : "text-slate-500"].join(" ")}>
          {nextActionForRow(row)}
        </span>
      </div>
      {waitingMeta ? (
        <div className="mt-2 rounded-md border border-slate-100 bg-slate-50 px-2 py-1.5 text-[11px] text-slate-600">
          <div className="flex items-center justify-between gap-2">
            <span>{waitingMeta.days} day{waitingMeta.days === 1 ? "" : "s"} waiting</span>
            {waitingMeta.stale ? <span className="h-2 w-2 rounded-full bg-rose-500" title="No reply since yesterday" /> : null}
          </div>
          <div className="mt-0.5 truncate">{waitingMeta.staff} · {waitingMeta.sentLabel}</div>
        </div>
      ) : null}
      {hasFollowUpStatusSplit(row) ? (
        <FollowUpStatusSplit row={row} currency={currency} compact />
      ) : null}
    </button>
  );
}

function FollowUpStatusSplit({ row, currency, compact = false, tutorialAnchor = false }) {
  const clinicalStatuses = continuationStatusesForRow(row);
  const paymentStatuses = moneyStatusesForRow(row);
  const balanceDue = Number(row?.balance_due_centavos || 0);
  const nextAppointmentLabel = row?.next_appointment_at ? formatDateShort(row.next_appointment_at) : null;

  if (!clinicalStatuses.length && !paymentStatuses.length) return null;

  const wrapperClass = compact
    ? "mt-2 grid gap-1.5 text-[10px] sm:grid-cols-2"
    : "mt-2 grid gap-2 text-[11px] sm:grid-cols-2";
  const panelClass = compact
    ? "rounded-md border px-2 py-1.5"
    : "rounded-lg border px-3 py-2";

  return (
    <div
      className={wrapperClass}
      data-testid={tutorialAnchor ? "recall-accepted-not-paid-statuses" : "recall-followup-status-split"}
      data-tutorial-id={tutorialAnchor ? "recall-accepted-not-paid-statuses" : undefined}
    >
      {clinicalStatuses.length ? (
        <div
          className={`${panelClass} border-teal-200 bg-teal-50 text-teal-900`}
          data-testid="recall-treatment-followup-status"
        >
          <div className="font-bold uppercase tracking-wide text-teal-700">Treatment</div>
          <div className="mt-1 flex flex-wrap gap-1">
            {clinicalStatuses.map((status) => (
              <span
                key={status}
                data-status-value={status}
                className="rounded-full border border-teal-200 bg-white/80 px-1.5 py-0.5 font-bold"
              >
                {labelizeStatus(status)}
              </span>
            ))}
          </div>
          {nextAppointmentLabel ? (
            <div className="mt-1 font-semibold text-teal-800">Next visit {nextAppointmentLabel}</div>
          ) : null}
        </div>
      ) : null}
      {paymentStatuses.length ? (
        <div
          className={`${panelClass} border-amber-200 bg-amber-50 text-amber-950`}
          data-testid="recall-payment-followup-status"
        >
          <div className="font-bold uppercase tracking-wide text-amber-700">Payment</div>
          <div className="mt-1 flex flex-wrap gap-1">
            {paymentStatuses.map((status) => (
              <span
                key={status}
                data-status-value={status}
                className="rounded-full border border-amber-200 bg-white/80 px-1.5 py-0.5 font-bold"
              >
                {labelizeStatus(status)}
              </span>
            ))}
          </div>
          {balanceDue > 0 ? (
            <div className="mt-1 font-semibold text-amber-900">{formatMoney(balanceDue, currency)} balance</div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function RecallWorkList({ rows, currency, cycleCap, onOpenRow }) {
  return (
    <div className="divide-y divide-slate-100" data-testid="recall-list" data-tutorial-id="recall-list">
      {rows.map((row) => {
        const bucket = bucketForRow(row);
        const actionLabel = bucket === "active"
          ? "Start follow-up"
          : bucket === "booked"
            ? "View booking"
            : "Review";
        return (
          <article
            key={row.id}
            data-testid={`recall-row-${row.id}`}
            className="grid gap-3 bg-white px-4 py-3.5 transition hover:bg-slate-50 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
          >
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <div className="truncate text-[14px] font-bold text-slate-950">{row.patient_name || "Unnamed"}</div>
                {bucket !== "active" ? <StateBadge state={row.current_state} /> : null}
                {riskForRow(row).label !== "Normal" ? <RiskBadge row={row} /> : null}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-slate-500">
                <span>{recallWorkLabel(row)}</span>
                <span className="text-slate-300">•</span>
                <span data-testid={`recall-row-doctor-${row.id}`}>Dr. {row.diagnosing_doctor_name || "Unassigned"}</span>
                <span className="text-slate-300">•</span>
                <span data-testid={`recall-row-cycle-${row.id}`}>Cycle {row.cycle_count}/{cycleCap}</span>
              </div>
              <div className="mt-1.5 text-[12px] text-slate-700">
                <span className="font-semibold">Next:</span> {nextActionForRow(row)}
                <span className="text-slate-400"> · {lastCallSummary(row) || `Diagnosed ${timeAgo(row.diagnosed_at)}`}</span>
              </div>
              {hasFollowUpStatusSplit(row) ? (
                <FollowUpStatusSplit row={row} currency={currency} compact />
              ) : null}
            </div>
            <div className="flex items-center justify-between gap-3 sm:justify-end">
              <span className="font-bold tabular-nums text-amber-700" data-testid={`recall-row-value-${row.id}`}>
                {formatMoney(row.value_centavos, currency)}
              </span>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onOpenRow(row);
                }}
                data-testid={`recall-work-action-${row.id}`}
                className={[
                  "rounded-md px-3 py-2 text-[12px] font-bold transition",
                  bucket === "active"
                    ? "bg-teal-600 text-white hover:bg-teal-700"
                    : "border border-slate-300 bg-white text-slate-700 hover:border-teal-300 hover:text-teal-800",
                ].join(" ")}
              >
                {actionLabel}
              </button>
            </div>
          </article>
        );
      })}
    </div>
  );
}

function RecallFlowModal({ row, currency, cycleCap, canWork, tutorialDemo = false, onClose, onAfterAction, onTutorialOutcome }) {
  if (!row) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[1190] flex items-center justify-center bg-slate-950/45 px-3 py-6" data-testid="recall-flow-modal" data-tutorial-id="recall-flow-modal">
      <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto py-1">
        <LoggingPanel
          row={row}
          currency={currency}
          cycleCap={cycleCap}
          canWork={canWork}
          tutorialDemo={tutorialDemo}
          followUpStatus={hasFollowUpStatusSplit(row) ? (
            <FollowUpStatusSplit row={row} currency={currency} tutorialAnchor />
          ) : null}
          onClose={onClose}
          onAfterAction={onAfterAction}
          onTutorialOutcome={onTutorialOutcome}
        />
      </div>
    </div>
  );
}

function RecallBucketConfirmation({ row, onCancel, onConfirm }) {
  if (!row) return null;
  const bucket = bucketForRow(row);
  return (
    <div className="fixed inset-0 z-[2147483645] flex items-center justify-center bg-slate-950/45 px-4 py-6" data-testid="recall-bucket-confirmation">
      <div className="w-full max-w-md rounded-lg border border-amber-200 bg-white shadow-2xl">
        <div className="border-b border-amber-100 bg-amber-50 px-4 py-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" strokeWidth={2.25} />
            <div>
              <h3 className="text-[15px] font-bold text-slate-950">Start recall from {bucketLabel(bucket)}?</h3>
              <p className="mt-1 text-[12px] leading-relaxed text-amber-900">
                {row.patient_name || "This patient"} is not in the Active pool. Confirm before starting outreach so the team knows you are intentionally overriding the bucket.
              </p>
            </div>
          </div>
        </div>
        <div className="px-4 py-3">
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
            <div className="text-[13px] font-bold text-slate-950">{row.patient_name || "Unnamed"}</div>
            <div className="mt-0.5 text-[12px] text-slate-500">{nextActionForRow(row)} · {formatDateShort(row.diagnosed_at)}</div>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md border border-slate-200 bg-white px-3 py-2 text-[13px] font-semibold text-slate-600 hover:bg-slate-50"
              data-testid="recall-confirm-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className="rounded-md bg-teal-600 px-3 py-2 text-[13px] font-semibold text-white hover:bg-teal-700"
              data-testid="recall-confirm-start"
            >
              Start recall flow
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BookedRecallDetails({ row, currency, onClose, onChangeBooking }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const bookedWork = useBookedRecallWork(row);

  useEffect(() => {
    let cancelled = false;
    async function loadHistory() {
      if (!row?.id) {
        setHistory([]);
        return;
      }
      setLoading(true);
      const { data, error } = await supabase.rpc("list_recall_attempts", { p_recall_queue_id: row.id });
      if (cancelled) return;
      setLoading(false);
      if (error) {
        console.warn("[recall] booked details history:", error.message);
        setHistory([]);
        return;
      }
      setHistory(Array.isArray(data) ? data : []);
    }
    loadHistory();
    return () => { cancelled = true; };
  }, [row?.id]);

  if (!row) return null;

  const bookedAttempts = history.filter((attempt) => attempt.outcome_detail === "booked");
  const latestBooked = bookedAttempts[0] || null;
  const convertedAt = latestBooked?.attempted_at || row.last_contacted_at || row.entered_queue_at;
  const recoveredValue = latestBooked?.value_centavos_at_attempt ?? row.value_centavos;
  const channel = latestBooked?.channel ? latestBooked.channel.replace("_", " ") : null;
  const staff = latestBooked?.staff_email || "Staff";

  return (
    <div className="fixed inset-0 z-[2147483645] flex items-center justify-center bg-slate-950/45 px-4 py-6" data-testid="booked-recall-details">
      <div className="w-full max-w-lg overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl">
        <div className="border-b border-emerald-100 bg-emerald-50 px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-emerald-700" strokeWidth={2.25} />
                <span className="text-[11px] font-bold uppercase tracking-wide text-emerald-700">Booked recall</span>
              </div>
              <h3 className="mt-1 truncate text-[18px] font-bold text-slate-950">{row.patient_name || "Unnamed"}</h3>
              <p className="mt-1 text-[12px] text-emerald-900">
                Future needs converted into a booked appointment.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-emerald-200 bg-white text-slate-500 hover:bg-emerald-50 hover:text-slate-800"
              aria-label="Close booked recall details"
              data-testid="booked-recall-close"
            >
              x
            </button>
          </div>
        </div>

        <div className="space-y-3 px-4 py-4">
          <div className="grid gap-2 sm:grid-cols-2">
            <BookedDetailStat label="Recovered value" value={formatMoney(recoveredValue, currency)} />
            <BookedDetailStat label="Converted" value={formatDateShort(convertedAt)} />
          </div>

          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
            <div className="text-[12px] font-semibold text-slate-700">Booking source</div>
            <div className="mt-1 text-[13px] text-slate-900">
              {loading
                ? "Loading conversion details..."
                : latestBooked
                  ? `${staff} converted this from ${channel || "recall"} on ${formatDateTimeShort(latestBooked.attempted_at)}.`
                  : "Converted from recall. Detailed attempt metadata is not available for this row."}
            </div>
            {latestBooked?.notes ? (
              <div className="mt-1 text-[12px] text-slate-500">{latestBooked.notes}</div>
            ) : null}
          </div>

          <div className="rounded-md border border-slate-200 bg-white px-3 py-2">
            <div className="text-[12px] font-semibold text-slate-700">Booked work</div>
            <BookedWorkList
              loading={bookedWork.loading}
              items={bookedWork.items}
              currency={currency}
              fallback={recallWorkLabel(row)}
            />
          </div>

          <div className="flex flex-wrap justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-200 bg-white px-3 py-2 text-[13px] font-semibold text-slate-600 hover:bg-slate-50"
            >
              Close
            </button>
            <button
              type="button"
              onClick={() => onChangeBooking(row, bookedWork)}
              className="inline-flex items-center gap-1.5 rounded-md bg-teal-600 px-3 py-2 text-[13px] font-semibold text-white hover:bg-teal-700"
              data-testid="booked-recall-change-booking"
            >
              <CalendarPlus className="h-3.5 w-3.5" strokeWidth={2.25} />
              Change booking
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RecallChangeBookingDialog({ context, currency, onClose, onChanged }) {
  const [chairSetupMinutes, setChairSetupMinutes] = useState(15);
  const recallRowId = context?.row?.id;

  useEffect(() => {
    if (!recallRowId) return;
    let cancelled = false;
    supabase.rpc("get_appointment_settings").then(({ data }) => {
      if (cancelled) return;
      const minutes = Number(data?.chair_setup_minutes);
      if (Number.isFinite(minutes) && minutes >= 0) {
        setChairSetupMinutes(minutes);
      }
    });
    return () => { cancelled = true; };
  }, [recallRowId]);

  if (!context?.row || !context?.bookedWork?.appointment) return null;

  const { row, bookedWork } = context;
  const appointment = bookedWork.appointment;
  const items = bookedWork.items || [];
  const duration = appointmentDurationMinutes(appointment);
  const durationBreakdown = recallDurationBreakdown(items, chairSetupMinutes);
  const recommendedDuration = durationBreakdown.recommendedMinutes > 0
    ? durationBreakdown.recommendedMinutes
    : duration;
  const value = items.length
    ? items.reduce((sum, item) => sum + Number(bookedNeedFee(item) || 0), 0)
    : Number(row.value_centavos || 0);

  return (
    <BookFutureNeedDialog
      open={true}
      onOpenChange={(next) => { if (!next) onClose?.(); }}
      bundle={{
        procedure_name: bookedRecallProcedureName(items),
        estimatedMinutes: recommendedDuration,
        durationBreakdown: durationBreakdown.recommendedMinutes > 0 ? durationBreakdown : null,
        needs: [],
        isAll: items.length > 1,
        categories: items.map((item) => procedureForNeed(item)?.category).filter(Boolean),
      }}
      patientId={row.patient_id}
      patientName={row.patient_name}
      defaultDoctorId={appointment.doctor_id}
      defaultChairId={appointment.chair_id}
      appointmentStatus={appointment.status || "Scheduled"}
      rescheduleAppointment={appointment}
      headingOverride="Change recall booking"
      subtitleDurationContext="Booked recall"
      submitLabel="Save booking"
      initialNotes="Recall booking change"
      appointmentSummary={(
        <div className="rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-2 text-[12px] text-emerald-900">
          <div className="font-semibold text-emerald-950">Recovered work: {formatMoney(value, currency)}</div>
          <div className="mt-0.5 text-emerald-800">
            {items.length ? items.map(bookedNeedLine).join(" · ") : recallWorkLabel(row)}
          </div>
        </div>
      )}
      onRescheduled={onChanged}
    />
  );
}

function BookedDetailStat({ label, value }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2">
      <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-0.5 text-[14px] font-bold text-slate-950">{value || "-"}</div>
    </div>
  );
}

function BookedWorkList({ loading, items, currency, fallback }) {
  if (loading) {
    return <div className="mt-1 text-[13px] text-slate-500">Loading booked procedures...</div>;
  }

  if (!items?.length) {
    return (
      <div className="mt-1 text-[13px] text-slate-900">
        {fallback}
      </div>
    );
  }

  return (
    <div className="mt-2 space-y-1.5">
      {items.map((item) => (
        <div key={item.id} className="flex items-start justify-between gap-3 rounded-md bg-slate-50 px-2.5 py-2">
          <div className="min-w-0">
            <div className="truncate text-[13px] font-semibold text-slate-950">{bookedNeedName(item)}</div>
            <div className="mt-0.5 text-[11px] text-slate-500">
              {bookedNeedSite(item) || "No tooth specified"} · {item.status || "Scheduled"}
            </div>
          </div>
          <div className="shrink-0 text-[12px] font-bold tabular-nums text-amber-700">
            {formatMoney(bookedNeedFee(item), currency)}
          </div>
        </div>
      ))}
    </div>
  );
}

function StateBadge({ state }) {
  const styles = {
    "Active": "bg-teal-50 text-teal-700 border-teal-200",
    "waiting_for_reply": "bg-sky-50 text-sky-700 border-sky-200",
    "Awaiting Reply": "bg-sky-50 text-sky-700 border-sky-200",
    "Follow-up Scheduled": "bg-violet-50 text-violet-700 border-violet-200",
    "Cooling Down": "bg-violet-50 text-violet-700 border-violet-200",
    "Cold Pile": "bg-slate-100 text-slate-700 border-slate-200",
    "Paused": "bg-amber-50 text-amber-700 border-amber-200",
    "Booked": "bg-emerald-50 text-emerald-700 border-emerald-200",
  };
  return (
    <span className={["inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-bold", styles[state] || styles.Active].join(" ")}>
      {state === "waiting_for_reply" ? "Waiting for Reply" : (state || "Active")}
    </span>
  );
}

function RiskBadge({ row }) {
  const risk = riskForRow(row);
  const styles = {
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    sky: "bg-sky-50 text-sky-700 border-sky-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    slate: "bg-slate-100 text-slate-700 border-slate-200",
  };
  return (
    <span className={["inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-bold", styles[risk.tone]].join(" ")}>
      {risk.tone === "rose" ? <Flame className="h-3 w-3" strokeWidth={2.25} /> : null}
      {risk.label}
    </span>
  );
}

function EmptyState({ bucket }) {
  return (
    <div className="p-4">
      <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-4 py-12 text-center" data-testid="recall-empty">
        <Users className="mx-auto h-6 w-6 text-teal-500" strokeWidth={2.25} />
        <h3 className="mt-3 text-[14px] font-bold text-slate-800">No {bucketLabel(bucket).toLowerCase()} patients.</h3>
        <p className="mt-1 text-[12px] text-slate-500">When patients enter this recall bucket, they will show up here.</p>
      </div>
    </div>
  );
}
